import React, { useState, useCallback, useEffect } from 'react';
import { Upload, X } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NFTUploader = ({ onUploadComplete, allowMultiple = true, selectedAssets = [] }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [selectedThumbs, setSelectedThumbs] = useState([]);

  // Objective F — hydrate visible thumbnails for artwork already selected
  // in prior wizard sessions so Back → Continue restores the actual
  // selected images rather than only a count.
  useEffect(() => {
    if (!selectedAssets || selectedAssets.length === 0) {
      setSelectedThumbs([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BACKEND_URL}/api/nft/gallery`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const data = await res.json();
        const byId = new Map();
        (data.nfts || []).forEach((n) => {
          const id = n.id || n.file_id;
          if (id) byId.set(id, n);
        });
        const thumbs = selectedAssets
          .map((id) => byId.get(id))
          .filter(Boolean)
          .map((n) => ({
            id: n.id || n.file_id,
            url: n.image_url || n.url || (n.file_id ? `/api/nft/file/${n.file_id}` : ''),
            filename: n.filename || n.name || 'Selected artwork',
          }));
        if (!cancelled) setSelectedThumbs(thumbs);
      } catch (_) {
        /* ignore — banner still shows count fallback */
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAssets && selectedAssets.join('|')]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files);
  };

  const handleFiles = async (files) => {
    setUploading(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      if (file.type.startsWith('image/')) formData.append('files', file);
    });
    try {
      const endpoint = allowMultiple
        ? `${BACKEND_URL}/api/nft/upload-bulk`
        : `${BACKEND_URL}/api/nft/upload-single`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        const newFiles = allowMultiple ? data.files : [data];
        setUploadedFiles((prev) => [...prev, ...newFiles]);
        if (onUploadComplete) onUploadComplete(newFiles);
      } else {
        alert('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload error');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (fileId) => {
    setUploadedFiles((prev) => prev.filter((f) => f.file_id !== fileId));
  };

  return (
    <div>
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${dragActive ? theme.colors.primary : theme.colors.borders}`,
          borderRadius: theme.radius.md,
          padding: '40px 20px',
          textAlign: 'center',
          background: dragActive ? `${theme.colors.primary}10` : theme.colors.bg.secondary,
          transition: 'all 0.2s',
          cursor: 'pointer',
        }}
      >
        <input
          type="file"
          multiple={allowMultiple}
          accept="image/*"
          onChange={handleFileInput}
          style={{ display: 'none' }}
          id="nft-file-input"
        />
        <label htmlFor="nft-file-input" style={{ cursor: 'pointer', display: 'block' }}>
          <Upload size={48} style={{ margin: '0 auto 16px', color: theme.colors.primary }} />
          <p style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '8px' }}>
            {allowMultiple ? 'Drop your artwork images here or use the button below' : 'Upload Artwork'}
          </p>
          <p style={{ fontSize: '13px', color: theme.colors.text.secondary, marginBottom: '14px' }}>
            {allowMultiple ? 'Supports: JPG, PNG, GIF, WebP (up to 100 files)' : 'Supports: JPG, PNG, GIF, WebP'}
          </p>
          <span data-testid="nft-uploader-choose-files" style={{
            display: 'inline-block', padding: '10px 18px',
            background: theme.colors.primary, color: '#FFFFFF',
            borderRadius: theme.radius.sm, fontSize: 13, fontWeight: 700,
          }}>Choose Files</span>
        </label>
      </div>

      {uploading && (
        <div style={{ marginTop: '16px', padding: '12px', background: theme.colors.bg.secondary, borderRadius: theme.radius.sm, textAlign: 'center', color: theme.colors.text.secondary }}>
          Uploading files...
        </div>
      )}

      {/* Objective F — persistent selection panel showing actual thumbnails
          from prior wizard sessions. Count-only banner is used only if
          thumbnail hydration fails. */}
      {selectedAssets && selectedAssets.length > 0 && (
        <div data-testid="nft-uploader-previously-selected" style={{
          marginTop: '16px', padding: '12px 14px', background: '#EEF2FF',
          border: '1px solid #6366F1', borderRadius: theme.radius.sm,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#3730A3' }}>
            {selectedAssets.length} image{selectedAssets.length === 1 ? '' : 's'} already selected for this Kickback
          </div>
          <div style={{ fontSize: 12, color: '#4338CA', marginTop: 2, marginBottom: 10 }}>
            Your selection is preserved. You can upload additional artwork below or switch to the Gallery tab to adjust it.
          </div>
          {selectedThumbs.length > 0 ? (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8,
            }}>
              {selectedThumbs.map((t) => (
                <div key={t.id} data-testid="nft-uploader-selected-thumb" style={{
                  position: 'relative', border: '1px solid #C7D2FE',
                  borderRadius: theme.radius.sm, padding: 4, background: '#FFF',
                }}>
                  <img
                    src={t.url && t.url.startsWith('http') ? t.url : `${BACKEND_URL}${t.url}`}
                    alt={t.filename}
                    style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 4 }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#4338CA' }}>
              Loading thumbnails…
            </div>
          )}
        </div>
      )}

      {uploadedFiles.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h4 data-testid="nft-uploader-session-count" style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '4px' }}>
            Uploaded in this session ({uploadedFiles.length})
          </h4>
          <p style={{ fontSize: '12px', color: theme.colors.text.muted, marginBottom: '12px' }}>
            These images are added to your Kickback selection below.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
            {uploadedFiles.map((file) => (
              <div key={file.file_id} style={{ position: 'relative', border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm, padding: '8px', background: theme.colors.bg.card }}>
                <img
                  src={`${BACKEND_URL}${file.url}`}
                  alt={file.filename}
                  style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: theme.radius.sm, marginBottom: '8px' }}
                />
                <p style={{ fontSize: '11px', color: theme.colors.text.secondary, textAlign: 'center', wordBreak: 'break-word' }}>
                  {file.filename}
                </p>
                <button
                  onClick={() => removeFile(file.file_id)}
                  style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#FFFFFF' }}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NFTUploader;
